import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';
import { toast } from 'react-toastify';

import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import api from '@/services/apiSgft';
import { PositionTrainingMatrixFilters } from '@/state/PositionTrainingMatrixFilter.atom';
import { RegulatoryTrainingMatrix } from '@/types/Training';

interface TrainingMatrixResponse {
	trainingMatrix: RegulatoryTrainingMatrix;
	totalResults: number;
	nextPage: number | null;
}

interface UseTrainingMatrixProps {
	filteredData: PositionTrainingMatrixFilters;
	matrixRefetch: boolean;
	isPositionFilteringIndicator: boolean;
	isTrainingFilteringIndicator: boolean;
	setIsMatrixChanging: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTrainingMatrix = ({
	filteredData,
	matrixRefetch,
	isPositionFilteringIndicator,
	isTrainingFilteringIndicator,
	setIsMatrixChanging,
}: UseTrainingMatrixProps) => {
	const fetchTrainingMatrix = async ({
		pageParam = 1,
	}): Promise<TrainingMatrixResponse> => {
		setIsMatrixChanging(true);
		try {
			const response = await api.get(
				`position-regulatory-training-link/matrix?size=20&page=${pageParam}`,
				{
					params: {
						positions: filteredData.position,
						locations: filteredData.location,
						trainingType: [TrainingTypeEnum.REGULATORY],
						management: filteredData.management,
						areaCoordination: filteredData.areaCoordination,
						trainings: filteredData.training,
						onlyUnmappedPositions: isPositionFilteringIndicator,
						onlyUnmappedTrainings: isTrainingFilteringIndicator,
					},
				},
			);
			return response.data;
		} catch (error) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: `error-${'position-regulatory-training-link/matrix'}`,
			});
			throw error;
		} finally {
			setIsMatrixChanging(false);
		}
	};

	const infiniteQueryResult = useInfiniteQuery(
		['trainings-matrix', filteredData, matrixRefetch],
		fetchTrainingMatrix,
		{
			retry: false,
			getNextPageParam: (actualPage) => actualPage.nextPage,
		},
	);

	const { data, ...rest } = infiniteQueryResult;

	const trainingsData = data?.pages
		.flatMap((page) => page.trainingMatrix)
		.reduce(
			(acc, curr) => {
				const validIndexes = curr.positions
					.map((position, index) =>
						position.areaCoordination !== null &&
						position.management !== null &&
						position.name !== null
							? index
							: -1,
					)
					.filter((index) => index !== -1);

				curr.positions = validIndexes.map((index) => curr.positions[index]);
				curr.matrix = validIndexes.map((index) => curr.matrix[index]);
				curr.blockMatrix = validIndexes.map((index) => curr.blockMatrix[index]);

				acc.positions.push(...curr.positions);
				const trainingSet = new Set<(typeof curr.trainings)[0]>([
					...curr.trainings,
				]);
				acc.trainings = [...trainingSet];
				acc.matrix.push(...curr.matrix);
				acc.blockMatrix.push(...curr.blockMatrix);

				return acc;
			},
			{
				positions: [],
				trainings: [],
				matrix: [],
				blockMatrix: [],
			} as RegulatoryTrainingMatrix,
		);

	return {
		trainingsData,
		...rest,
	};
};
